/**
 * defa.views.UserContainer
 * The main Container for the UserController.
 * @version 1.2.1
 */
import PhoneNumberContainer         from 'scripts/defa/phone-numbers/view-phone-number-container';
import UserCreateForm               from 'scripts/defa/users/view-user-create-form';
import UserEmailForm                from 'scripts/defa/users/view-user-email-form';
import UserForgotPasswordForm       from 'scripts/defa/users/view-user-forgot-password-form';
import UserLanguageForm             from 'scripts/defa/users/view-user-language-form';
import UserLoginCreateDualContainer from 'scripts/defa/users/view-user-login-create-dual-container';
import UserLoginForm                from 'scripts/defa/users/view-user-login-form';
import UserPasswordForm             from 'scripts/defa/users/view-user-password-form';
import UserSettingsPanel            from 'scripts/defa/users/view-user-settings-panel';
import UserThankYouView             from 'scripts/defa/users/view-user-thank-you-view';
import Container                    from 'scripts/poppet/container';
import template                     from 'templates/userContainer.jst.html';

const channelNav    = Bb.Radio.channel( 'nav' ),
	channelUser   = Bb.Radio.channel( 'user' );

const selectors = {
	region  : '[data-region=user-container]',
};

const UserContainer = Container.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	template: template,
	
	regions: {
		container: selectors.region,
	},
	
	
	// ------------------------------------------------
	// Event Handlers
	// ------------------------------------------------
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	// - public methods
	
	checkRequirements: function () {
		
		switch ( this.options.view ) {
			
			case 'user:create' :
			case 'user:forgot' :
			case 'user:login'  :
				if ( channelUser.request( 'is:logged:in' ) ) {
					channelNav.trigger( 'unit:list', false, { trigger: true, replace: true, } );
					return false;
				}
				break;
			
		}
		
		return true;
	},
	
	/**
	 * Attaches a designated view into the container region.
	 * @param {Object} options  All the options for this Layout, must contain a key->value pair named view, where the
	 *     value is a string.
	 * @void
	 */
	attachView: function () {
		// prevent create, forgot, and login from bleeding listeners.
		this.stopListening( this.options.user, 'sync' );
		
		switch ( this.options.view ) {
			
			case 'user:panel' :
				this._showSettingsPanel();
				break;
			
			case 'user:language' :
				this._showLanguageView();
				break;
			
			case 'user:password' :
				this._showPasswordView();
				break;
				
			case 'user:email' :
				this._showEmailView();
				break;
			
			case 'user:phone' :
				this._showPhoneView();
				break;
			
			case 'user:forgot' :
				this._showForgotView();
				break;
			
			case 'user:login' :
				this._showLoginForm();
				break;
			
			case 'user:logincreate' :
				this._showLoginCreateForm();
				break;
			
			case 'user:thank-you' :
				this._showThankYouView();
				break;
			
			case 'user:create' :
				this._showCreateForm();
				break;
			
			default :
				this._showLoginCreateForm();
				break;
		}
	},
	
	// - private methods
	
	/**
	 * Shows the create new user view.
	 * @void
	 */
	_showCreateForm: function () {
		
		let userCreateForm = new UserCreateForm( _.extend( this.options, {
			model: this.options.currentUser,
		} ) );
		
		this.showChildView( 'container', userCreateForm );
	},
	
	/**
	 * Shows the forgot password view.
	 * @void
	 */
	_showForgotView: function () {
		
		let userForgotPasswordView = new UserForgotPasswordForm( _.extend( this.options, {
			model: this.options.user,
		} ) );
		
		this.showChildView( 'container', userForgotPasswordView );
	},
	
	/**
	 * Shows the login user view.
	 * @void
	 */
	_showLoginForm: function () {
		
		let userLoginForm = new UserLoginForm( {
			model: this.options.user,
			email: this.options.email,
		} );
		
		// add an additional busy-spin, in case this is the primary login->redirect flow.
		this.listenToOnce( this.options.user, 'sync', function () {
			userLoginForm.busy();
			
			// external navhandler does login reload
			if ( 'external' === this.navHandler ) {
				channelNav.trigger( 'unit:list', false, { reload: true } );
			}
		} );
		
		this.showChildView( 'container', userLoginForm );
	},
	
	/**
	 * Shows a dual view of both login and create.
	 * @void
	 */
	_showLoginCreateForm: function () {
		
		let userLoginCreateDualContainer = new UserLoginCreateDualContainer( this.options );
		this.showChildView( 'container', userLoginCreateDualContainer, this.closeLink );
	},
	
	/**
	 * Shows the thank you view.
	 * @void
	 */
	_showThankYouView: function () {
		
		let userThankYouView = new UserThankYouView( _.extend( this.options, {
			model: this.options.currentUser,
		} ) );
		
		this.showChildView( 'container', userThankYouView );
	},
	
	/**
	 * Shows the main view for updating users.
	 * @void
	 */
	_showSettingsPanel: function () {
		
		let userSettingsPanel = new UserSettingsPanel( _.extend( this.options, {
			model: this.options.currentUser,
		} ) );
		
		this.showChildView( 'container', userSettingsPanel );
	},
	
	/**
	 * Shows the change language view.
	 * @void
	 */
	_showLanguageView: function () {
		
		let userLanguageForm = new UserLanguageForm( _.extend( this.options, {
			model: this.options.currentUser,
		} ) );
		
		// if the user saves a new language, reload application to fetch the language.
		this.listenToOnce( this.options.currentUser, 'sync', function () {
			if ( channelUser.request( 'id' ) === this.options.currentUser.get( 'id' ) ) {
				
				this.listenTo( userLanguageForm, 'animateOut', function () {
					_.extend( userLanguageForm.closeLink, {
						options: {
							reload: true,
						}
					} );
				} );
				
				userLanguageForm.animateOut();
			}
		} );
		
		this.listenTo( userLanguageForm, 'destroy', function () {
			this.stopListening( this.options.currentUser, 'sync' );
		} );
		
		this.showChildView( 'container', userLanguageForm );
	},
	
	/**
	 * Shows the change password view.
	 * @void
	 */
	_showPasswordView: function () {
		
		let userPasswordForm = new UserPasswordForm( _.extend( this.options, {
			model: this.options.currentUser,
		} ) );
		this.showChildView( 'container', userPasswordForm );
	},
	
	_showEmailView: function() {
		let userEmailForm = new UserEmailForm( _.extend( this.options, {
			model: this.options.currentUser
		}));
		this.showChildView('container', userEmailForm)
	},
	
	/**
	 * Shows the phone layout, with options.
	 * @param {object} options All options needed for starting the phone layout.
	 * @void
	 */
	_showPhoneView: function () {
		
		// if the phoneNumberContainer isn't made or is destroyed, create it.
		if ( !( this.phoneNumberContainer instanceof PhoneNumberContainer ) || this.phoneNumberContainer.isDestroyed() ) {
			this.phoneNumberContainer = new PhoneNumberContainer( this.options );
			
			// if it already exists, just update it's options.
		} else {
			this.phoneNumberContainer.settings( this.options );
		}
		
		// Attach the phoneNumberContainer to the DOM.
		if ( !this.phoneNumberContainer.isAttached() ) {
			this.showChildView( 'container', this.phoneNumberContainer );
		}
	},
	
} );

export default UserContainer;